import React, { useCallback, useMemo, useState } from 'react';
import { AlertCircle, Ruler, X } from 'lucide-react';
import { message } from 'antd';

import { updateQuickInferencePixelSpacing } from '../../../helpers/api/quick-inference/quick-inference';

interface Props {
  inferenceId: string;
  onCalibrated: () => void | Promise<void>;
  /** required = needs spacing; optional = compact change control when already calibrated */
  mode?: 'required' | 'optional';
  /** Current spacing in mm/pixel when already calibrated (prefill change form). */
  initialSpacingMm?: number | null;
}

function formatInitialSpacing(mm: number | null | undefined): string {
  if (mm == null || !Number.isFinite(mm) || mm <= 0) return '';
  // Prefer short display without forcing "1"
  const rounded = Math.round(mm * 10000) / 10000;
  return String(rounded);
}

export function QuickInferenceCalibrationBar({
  inferenceId,
  onCalibrated,
  mode = 'required',
  initialSpacingMm = null,
}: Props) {
  const [spacingText, setSpacingText] = useState(() => formatInitialSpacing(initialSpacingMm));
  const [unit, setUnit] = useState<'mm' | 'cm'>('mm');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const spacingValue = useMemo(() => {
    const normalized = spacingText.trim().replace(',', '.');
    if (!normalized) return NaN;
    return Number(normalized);
  }, [spacingText]);

  const canApply = Number.isFinite(spacingValue) && spacingValue > 0;

  const openEditor = useCallback(() => {
    setSpacingText(formatInitialSpacing(initialSpacingMm));
    setUnit('mm');
    setExpanded(true);
  }, [initialSpacingMm]);

  const handleApply = useCallback(async () => {
    if (!canApply) {
      message.warning('Enter a pixel spacing greater than zero');
      return;
    }
    setSaving(true);
    try {
      await updateQuickInferencePixelSpacing(inferenceId, {
        pixelSpacing: spacingValue,
        pixelSpacingUnit: unit,
      });
      message.success('Pixel spacing saved — measurements now shown in cm');
      setExpanded(false);
      await onCalibrated();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || err?.message || 'Failed to save spacing');
    } finally {
      setSaving(false);
    }
  }, [canApply, inferenceId, spacingValue, unit, onCalibrated]);

  const stopCanvasShortcuts = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  if (!expanded) {
    if (mode === 'optional') {
      return (
        <button
          type="button"
          onClick={openEditor}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50/70 px-2.5 py-1.5 text-[11px] font-medium text-blue-700 transition hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/40"
        >
          <Ruler size={12} />
          Change calibration
        </button>
      );
    }

    return (
      <div className="overflow-hidden rounded-lg border border-yellow-200 bg-gradient-to-br from-yellow-50 to-amber-50 dark:border-yellow-700 dark:from-yellow-900/20 dark:to-amber-900/10">
        <div className="flex items-start gap-2.5 p-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-400">
            <AlertCircle size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-xs font-semibold text-yellow-900 dark:text-yellow-200">
              Calibration required
            </h4>
            <p className="mt-0.5 text-[11px] leading-relaxed text-yellow-800/80 dark:text-yellow-300/80">
              Set pixel spacing to show measurements in cm.
            </p>
            <button
              type="button"
              onClick={openEditor}
              className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-yellow-400 px-3 py-2 text-xs font-semibold text-yellow-950 shadow-sm transition hover:bg-yellow-500"
            >
              <Ruler size={13} />
              Calibrate now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 to-sky-50 dark:border-blue-700 dark:from-blue-900/25 dark:to-sky-900/15"
      onKeyDown={stopCanvasShortcuts}
      onKeyUp={stopCanvasShortcuts}
    >
      <div className="flex items-center justify-between gap-2 border-b border-blue-100 px-3 py-2 dark:border-blue-800/50">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300">
            <Ruler size={12} />
          </div>
          <p className="truncate text-xs font-semibold text-blue-900 dark:text-blue-200">
            Pixel spacing
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-blue-400 transition hover:bg-blue-100 hover:text-blue-600 dark:hover:bg-blue-900/40"
          aria-label="Close calibration"
        >
          <X size={13} />
        </button>
      </div>

      <div className="space-y-2.5 p-3">
        <div className="grid grid-cols-1 gap-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-blue-700/80 dark:text-blue-300/80">
              Spacing value
            </span>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={spacingText}
              onChange={(e) => setSpacingText(e.target.value)}
              onKeyDown={stopCanvasShortcuts}
              placeholder="e.g. 0.15"
              className="h-9 w-full rounded-lg border border-blue-200 bg-white px-2.5 text-sm font-medium text-slate-800 outline-none ring-blue-300 transition focus:border-blue-400 focus:ring-2 dark:border-blue-800 dark:bg-gray-900 dark:text-gray-100"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-blue-700/80 dark:text-blue-300/80">
              Unit
            </span>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as 'mm' | 'cm')}
              onKeyDown={stopCanvasShortcuts}
              className="h-9 w-full rounded-lg border border-blue-200 bg-white px-2.5 text-sm font-medium text-slate-800 outline-none ring-blue-300 transition focus:border-blue-400 focus:ring-2 dark:border-blue-800 dark:bg-gray-900 dark:text-gray-100"
            >
              <option value="mm">mm / pixel</option>
              <option value="cm">cm / pixel</option>
            </select>
          </label>
        </div>

        <p className="text-[10px] leading-relaxed text-blue-700/70 dark:text-blue-300/60">
          Typical ultrasound: 0.1–0.3 mm/pixel.
        </p>

        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={handleApply}
            disabled={saving || !canApply}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <>
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Saving…
              </>
            ) : (
              <>
                <Ruler size={12} />
                Apply
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="h-7 rounded-lg text-[11px] font-medium text-slate-500 transition hover:bg-white/60 hover:text-slate-700 dark:text-gray-400 dark:hover:bg-gray-800/40"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
